import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { listFrom } from '../../api/board';
import { useApp } from '../../context/AppContext';
import { useI18n } from '../../i18n/useI18n';
import { useUi } from '../../context/UiContext';
import { formatDateLabel, fromKey } from '../../utils/dates';
import { textDir } from '../../utils/text';
import { IconCheckSquare, IconLink } from '../icons/Icons';
import { Bullet } from './TaskColumn';
import styles from './detail.module.css';

const ALLOWED = {
  B: new Set(),
  STRONG: new Set(),
  I: new Set(),
  EM: new Set(),
  BR: new Set(),
  DIV: new Set(),
  P: new Set(),
  UL: new Set(),
  OL: new Set(),
  LI: new Set(),
  SPAN: new Set(),
  LABEL: new Set(),
  A: new Set(['href', 'target', 'rel']),
  IMG: new Set(['src', 'alt']),
  INPUT: new Set(['type', 'checked']),
};

function sanitize(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const clean = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) return;
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        return;
      }
      const allowed = ALLOWED[child.tagName];
      if (!allowed) {
        clean(child);
        const parent = child.parentNode;
        while (child.firstChild) parent.insertBefore(child.firstChild, child);
        child.remove();
        return;
      }
      [...child.attributes].forEach((attr) => {
        if (!allowed.has(attr.name)) child.removeAttribute(attr.name);
      });
      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || '';
        if (!/^(https?:|mailto:)/i.test(href)) {
          clean(child);
          const parent = child.parentNode;
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          child.remove();
          return;
        }
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
      if (child.tagName === 'IMG') {
        const src = child.getAttribute('src') || '';
        if (!/^(https?:|data:image\/(?:png|jpe?g|gif|webp);)/i.test(src)) {
          child.remove();
          return;
        }
      }
      if (child.tagName === 'INPUT' && child.getAttribute('type') !== 'checkbox') {
        child.remove();
        return;
      }
      clean(child);
    });
  };
  clean(template.content);
  const holder = document.createElement('div');
  holder.append(...template.content.childNodes);
  return holder.innerHTML;
}

function checkboxLine(node, editor) {
  let el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  while (el && el !== editor) {
    if (el.querySelector(':scope > input[type="checkbox"]')) return el;
    el = el.parentElement;
  }
  return null;
}

function ensureCheckSpan(line) {
  const input = line.querySelector(':scope > input[type="checkbox"]');
  if (!input) return null;
  input.contentEditable = 'false';
  let span = input.nextElementSibling;
  if (!span || span.tagName !== 'SPAN') {
    span = document.createElement('span');
    while (input.nextSibling) span.appendChild(input.nextSibling);
    if (!span.textContent) span.textContent = '\u00a0';
    input.after(span);
  }
  return span;
}

function prepareChecks(editor) {
  editor.querySelectorAll('input[type="checkbox"]').forEach((input) => {
    if (input.parentElement) ensureCheckSpan(input.parentElement);
  });
}

function makeCheckLine(fragment) {
  const line = document.createElement('div');
  const box = document.createElement('input');
  box.type = 'checkbox';
  box.contentEditable = 'false';
  const span = document.createElement('span');
  const carried = fragment && fragment.textContent.replace(/\u00a0/g, ' ').trim();
  if (carried) span.appendChild(fragment);
  else span.textContent = '\u00a0';
  line.append(box, span);
  return line;
}

function placeCaret(node, offset) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

function readNotes(editor) {
  editor.querySelectorAll('input[type="checkbox"]').forEach((input) => {
    if (input.checked) input.setAttribute('checked', '');
    else input.removeAttribute('checked');
  });
  const html = sanitize(editor.innerHTML);
  const text = editor.textContent.replace(/\u00a0/g, ' ').trim();
  const hasMedia = editor.querySelector('img, input');
  const empty = !text && !hasMedia;
  editor.dataset.empty = empty ? 'true' : 'false';
  return empty ? '' : html;
}

function locate(state, detail, lang, listLabel) {
  if (!detail) return null;
  const tasks = listFrom(state, detail);
  let task = tasks.find((item) => item.id === detail.id);
  if (!task && detail.zone === 'day') {
    const series = (state.recurring || []).find((item) => item.id === detail.id);
    if (series) {
      task = {
        id: series.id,
        text: series.text,
        done: (series.doneOn || []).includes(detail.date),
        notes: series.notes || '',
      };
    }
  }
  if (!task) return null;
  if (detail.zone === 'day' && detail.date) {
    return { task, label: formatDateLabel(fromKey(detail.date), lang) };
  }
  const page = state.pages.find((item) => item.id === detail.pageId);
  const list = page?.lists.find((item) => item.id === detail.listId);
  const title = list?.title || listLabel;
  return { task, label: lang === 'ar' ? title : title.toUpperCase() };
}

const EXIT_MS = 260;

export function TaskDetail({ detail }) {
  const { state, actions } = useApp();
  const { lang, t } = useI18n();
  const { closeDetail } = useUi();
  const [view, setView] = useState(null);
  const [phase, setPhase] = useState('off');
  const phaseRef = useRef('off');
  const source = view || detail;
  const located = locate(state, source, lang, t('lists.list'));
  const task = located?.task;
  const editorRef = useRef(null);
  const titleRef = useRef(null);
  const rangeRef = useRef(null);
  const [marks, setMarks] = useState({ bold: false, italic: false });

  useEffect(() => {
    if (detail && !locate(state, detail, lang, t('lists.list'))) closeDetail();
  }, [detail, state, closeDetail]);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    if (detail) {
      setView(detail);
      if (reduce || phaseRef.current === 'on') {
        phaseRef.current = 'on';
        setPhase('on');
        return undefined;
      }
      if (phaseRef.current === 'out') {
        phaseRef.current = 'on';
        setPhase('on');
        return undefined;
      }
      phaseRef.current = 'in';
      const frame = window.requestAnimationFrame(() => {
        phaseRef.current = 'on';
        setPhase('on');
      });
      return () => window.cancelAnimationFrame(frame);
    }

    if (phaseRef.current === 'off') return undefined;
    if (reduce) {
      phaseRef.current = 'off';
      setPhase('off');
      setView(null);
      return undefined;
    }
    phaseRef.current = 'out';
    setPhase('out');
    const timer = window.setTimeout(() => {
      phaseRef.current = 'off';
      setPhase('off');
      setView(null);
    }, EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [detail]);

  useEffect(() => {
    if (!detail && phase === 'off') return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || document.body.classList.contains('is-dragging')) return;
      event.preventDefault();
      closeDetail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, phase, closeDetail]);

  useLayoutEffect(() => {
    const field = titleRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
  }, [task?.text, phase]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !task) return;
    editor.innerHTML = task.notes || '';
    prepareChecks(editor);
    editor.dataset.empty = task.notes ? 'false' : 'true';
  }, [task?.id]);

  if (!task || !source) return null;
  const shownPhase = phase === 'off' ? 'in' : phase;

  const loc = {
    zone: source.zone,
    date: source.date,
    pageId: source.pageId,
    listId: source.listId,
  };

  const commit = () => {
    const editor = editorRef.current;
    if (!editor) return;
    actions.updateNotes(loc, task.id, readNotes(editor));
  };

  const syncMarks = () => {
    try {
      setMarks({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
      });
    } catch {
      setMarks({ bold: false, italic: false });
    }
  };

  const rememberRange = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || !selection.rangeCount) return;
    if (!editor.contains(selection.anchorNode)) return;
    rangeRef.current = selection.getRangeAt(0);
  };

  const apply = (command, value = null) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (rangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(rangeRef.current);
    }
    document.execCommand(command, false, value);
    commit();
    syncMarks();
  };

  const insertCheckbox = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (rangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(rangeRef.current);
    }
    const line = makeCheckLine(null);
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (range && editor.contains(range.startContainer)) {
      range.deleteContents();
      range.insertNode(line);
    } else {
      editor.appendChild(line);
    }
    const span = line.querySelector('span');
    placeCaret(span, span.textContent === '\u00a0' ? 1 : 0);
    commit();
  };

  const onNotesKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.metaKey || event.ctrlKey) return;
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const line = checkboxLine(selection.anchorNode, editor);
    if (!line) return;
    event.preventDefault();
    const span = ensureCheckSpan(line);
    const text = span.textContent.replace(/\u00a0/g, ' ').trim();
    if (!text) {
      const plain = document.createElement('div');
      plain.appendChild(document.createElement('br'));
      line.replaceWith(plain);
      placeCaret(plain, 0);
      commit();
      return;
    }
    const range = selection.getRangeAt(0);
    const after = document.createRange();
    after.selectNodeContents(span);
    if (span.contains(range.endContainer)) after.setStart(range.endContainer, range.endOffset);
    const fragment = after.extractContents();
    const next = makeCheckLine(fragment);
    line.after(next);
    const nextSpan = next.querySelector('span');
    placeCaret(nextSpan, nextSpan.textContent === '\u00a0' ? 1 : 0);
    commit();
  };

  const addLink = () => {
    const entered = window.prompt(t('detail.linkPrompt'));
    if (!entered?.trim()) return;
    const href = /^(https?:|mailto:)/i.test(entered.trim())
      ? entered.trim()
      : `https://${entered.trim()}`;
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor) return;
    editor.focus();
    if (rangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(rangeRef.current);
    }
    const selected = selection?.toString() || '';
    if (selected) {
      document.execCommand('createLink', false, href);
    } else {
      const safeHref = href.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      const label = href.replace(/^https?:\/\//i, '').replace(/^mailto:/i, '');
      const safeLabel = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      document.execCommand(
        'insertHTML',
        false,
        `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`,
      );
    }
    const node = selection?.anchorNode;
    const anchor =
      node?.nodeType === Node.ELEMENT_NODE
        ? node.closest?.('a')
        : node?.parentElement?.closest?.('a');
    if (anchor instanceof HTMLAnchorElement) {
      anchor.setAttribute('href', href);
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
    commit();
    syncMarks();
  };

  const openNotesLink = (event) => {
    const editor = editorRef.current;
    if (!editor) return;
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    if (!anchor || !editor.contains(anchor)) return;
    const href = anchor.getAttribute('href') || '';
    if (!/^(https?:|mailto:)/i.test(href)) return;
    event.preventDefault();
    event.stopPropagation();
    if (/^mailto:/i.test(href)) {
      window.location.href = href;
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className={styles.scrim} data-phase={shownPhase} onClick={closeDetail} />
      <aside className={styles.panel} data-phase={shownPhase} role="dialog" aria-label={t('detail.title')}>
        <div className={styles.head} dir={textDir(task.text) || undefined}>
          <button
            type="button"
            className={styles.bullet}
            aria-label={task.done ? t('task.undone') : t('task.done')}
            aria-pressed={task.done}
            onClick={() => actions.toggleTask(loc, task.id)}
          >
            <Bullet type={state.preferences.bullet} done={task.done} />
          </button>
          <textarea
            ref={titleRef}
            className={task.done ? `${styles.title} ${styles.titleDone}` : styles.title}
            value={task.text}
            rows={1}
            aria-label={t('task.label')}
            onChange={(event) => actions.updateTask(loc, task.id, event.target.value)}
            onBlur={(event) => {
              if (!event.target.value.trim()) {
                actions.deleteTask(loc, task.id);
                closeDetail();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                editorRef.current?.focus();
              }
            }}
          />
        </div>
        <div className={styles.toolbar}>
          <div className={styles.tools}>
            <button
              type="button"
              className={styles.tool}
              data-active={marks.bold ? 'true' : 'false'}
              aria-label={t('detail.bold')}
              aria-pressed={marks.bold}
              onMouseDown={(event) => {
                event.preventDefault();
                rememberRange();
              }}
              onClick={() => apply('bold')}
            >
              B
            </button>
            <button
              type="button"
              className={`${styles.tool} ${styles.italic}`}
              data-active={marks.italic ? 'true' : 'false'}
              aria-label={t('detail.italic')}
              aria-pressed={marks.italic}
              onMouseDown={(event) => {
                event.preventDefault();
                rememberRange();
              }}
              onClick={() => apply('italic')}
            >
              i
            </button>
            <button
              type="button"
              className={styles.tool}
              aria-label={t('detail.link')}
              onMouseDown={(event) => {
                event.preventDefault();
                rememberRange();
              }}
              onClick={addLink}
            >
              <IconLink width="16" height="16" />
            </button>
            <button
              type="button"
              className={styles.tool}
              aria-label={t('detail.check')}
              onMouseDown={(event) => {
                event.preventDefault();
                rememberRange();
              }}
              onClick={insertCheckbox}
            >
              <IconCheckSquare width="16" height="16" />
            </button>
          </div>
          <p className={styles.date}>{located.label}</p>
        </div>
        <div
          ref={editorRef}
          className={styles.notes}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={t('detail.notes')}
          data-placeholder={t('detail.placeholder')}
          data-empty={task.notes ? 'false' : 'true'}
          spellCheck
          onInput={commit}
          onKeyDown={onNotesKeyDown}
          onKeyUp={syncMarks}
          onMouseUp={syncMarks}
          onClick={(event) => {
            if (event.target instanceof HTMLInputElement) commit();
            else openNotesLink(event);
          }}
          onAuxClick={(event) => {
            if (event.button === 1) openNotesLink(event);
          }}
        />
      </aside>
    </>
  );
}
